import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Statuses } from '@sysarb/sysarb-api/models/entities/EmploymentPerformanceReviewAdminOverview';
import type EmploymentPerformanceReviewAdminOverview from '@sysarb/sysarb-api/models/entities/EmploymentPerformanceReviewAdminOverview';
import type { AffectedPeople, DiscussionStatus, EmploymentDiscussion, Manager, PublishType } from '@sysarb/sysarb-types/services/v1/discussion';
import { EmploymentDiscussionStatus } from '@sysarb/sysarb-types/services/v1/discussion';

const useDiscussionStore = defineStore('discussion', () => {
    // State
    const _discussion = ref<EmploymentPerformanceReviewAdminOverview | null>(null);
    const _employmentDiscussions = ref<EmploymentDiscussion[] | null>([]);
    const _managers = ref<Manager[] | null>([]);
    const _discussionStatusKeyValues = ref<DiscussionStatus | null>(null);
    const _affectedPeople = ref<AffectedPeople[]>([]);

    // Getters
    const discussion = computed(() => _discussion.value);
    const discussionStatus = computed(() => _discussion.value?.status.value);
    const publishStatus = computed(() => _discussion.value?.publishedStatus.value);
    const employmentDiscussions = computed(() => _employmentDiscussions.value);
    const managers = computed(() => _managers.value);
    const ongoingDiscussions = computed(() => (_employmentDiscussions.value != null ? _employmentDiscussions.value.filter(x => x.DiscussionStatus === EmploymentDiscussionStatus.OngoingDiscussion).length : 0));
    const hasMissingManagers = computed(() => (_managers.value != null? _managers.value.some(x => x.ID == null) : true));
    const discussionStatusKeyValues = computed(() => _discussionStatusKeyValues.value);
    const affectedPeople = computed(() => _affectedPeople.value);

    // Actions
    function setDiscussion(discussion: EmploymentPerformanceReviewAdminOverview): void {
        _discussion.value = discussion;
    }

    function setDiscussionStatus(status: Statuses): void {
        if (_discussion.value == null) return;
        _discussion.value.status.value = status;
    }

    function setDiscussionPublishStatus(publishStatus: PublishType): void {
        if (_discussion.value == null) return;
        _discussion.value.publishedStatus = { value: publishStatus };
    }

    function setEmploymentDiscussions(employmentDiscussions: EmploymentDiscussion[]): void {
        _employmentDiscussions.value = employmentDiscussions;
    }

    function setManagers(managers: Manager[]): void {
        _managers.value = managers;
    }

    function setDiscussionStatusKeyValues(discussionStatusKeyValues: DiscussionStatus): void {
        _discussionStatusKeyValues.value = discussionStatusKeyValues;
    }

    function setAffectedPeople(affectedPeople: AffectedPeople[]): void {
        _affectedPeople.value = affectedPeople;
    }

    return {
        // State and getters
        discussion,
        discussionStatus,
        publishStatus,
        employmentDiscussions,
        managers,
        ongoingDiscussions,
        hasMissingManagers,
        discussionStatusKeyValues,
        affectedPeople,

        // Actions
        setDiscussion,
        setDiscussionStatus,
        setDiscussionPublishStatus,
        setEmploymentDiscussions,
        setManagers,
        setDiscussionStatusKeyValues,
        setAffectedPeople
    };
});

export { useDiscussionStore };
